<template>
  <div>
    <v-card>
      <v-card-title>
      <v-icon color="primary">
        mdi-bus-multiple
      </v-icon>
        <span class="pl-2">{{ $t('systemSetup.buses.pageTitle') }}</span>
        <v-spacer></v-spacer>
        <create-button @create="showBusDialog"></create-button>
        <activation-tool-tip model="buses"></activation-tool-tip>
      </v-card-title>
      <v-data-table
        item-key="id"
        :loading="isLoading"
        :loading-text="$t('systemSetup.buses.loadingText')"
        :headers="headers"
        :items="buses"
        :search="search"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            :label="$t('common.search')"
            class="mx-4"
          ></v-text-field>
        </template>
        <template v-slot:item.driver="{ item }">
          <v-chip :color="getDriverAssignmentColor(item.driver)" dark @click="assignDriver(item)">
            {{ getDriver(item.driver) }}
          </v-chip>
        </template>
        <template v-slot:item.created_at="{ item }">
          <small>{{ item.created_at | moment("LL") }}</small> -
          <small class="text-muted">{{ item.created_at | moment("LT") }}</small>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-icon v-if="item.driver" small class="mr-2" @click="unAssignDriver(item)">
            mdi-account-off
          </v-icon>
          <v-icon v-else small class="mr-2" @click="assignDriver(item)">
            mdi-account-tie-hat
          </v-icon>
          <v-icon small class="mr-2" @click="editBus(item)">
            mdi-pencil
          </v-icon>
          <v-icon small @click="deleteBus(item, buses.indexOf(item))">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
    <v-row justify="center">
      <v-dialog
        v-model="busDialog"
        persistent
        max-width="600px"
      >
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation>
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ $t('systemSetup.buses.dialogTitle') }}</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="license"
                      :rules="licenseRules"
                      :label="$t('systemSetup.buses.licensePlateLabel')"
                      :hint="$t('systemSetup.buses.licensePlateHint')"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                    md="4"
                  >
                    <v-text-field
                      v-model="capacity"
                      :rules="capacityRules"
                      :label="$t('systemSetup.buses.capacityLabel')"
                      :hint="$t('systemSetup.buses.capacityHint')"
                      required
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                @click="busDialog = false"
              >
                {{ $t('common.close') }}
              </v-btn>
              <v-btn
                color="blue darken-1"
                text
                @click="createBus"
              >
                {{ $t('common.create') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-form>
      </v-dialog>
    </v-row>
    <v-dialog v-if="selectedBus" v-model="driversDialog" max-width="390">
      <v-card>
        <v-card-title class="text-h5"> {{ $t('systemSetup.buses.selectDriverTitle', { license: selectedBus.license }) }} </v-card-title>

        <v-card-text>
          <v-list dense>
            <v-subheader>{{ $t('systemSetup.buses.driversSubheader') }}</v-subheader>
            <v-list-item-group>
              <v-list-item
                v-for="(driver, i) in availableDrivers"
                :key="i"
              >
                <v-list-item-content>
                  <v-list-item-title v-text="driver.name" @click="assignDriverToBus(driver)"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>
        <v-container style="height: 400px">
          <v-row
            v-show="loadingDrivers"
            class="fill-height"
            align-content="center"
            justify="center"
          >
            <v-col class="text-subtitle-1 text-center" cols="12">
              {{ $t('systemSetup.buses.pleaseWait') }}
            </v-col>
            <v-col cols="6">
              <v-progress-linear
                :active="loadingDrivers"
                color="primary"
                indeterminate
                rounded
                height="6"
              ></v-progress-linear>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            color="green darken-1"
            text
            @click="closeDriverDialog"
          >
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ActivationToolTip from "@/components/ActivationToolTip";
import CreateButton from "@/components/CreateButton";
import auth from '@/services/AuthService';
export default {
  components: {
    ActivationToolTip,
    CreateButton,
  },
  data() {
    return {
      buses: [],
      availableDrivers: [],
      isLoading: false,
      search: "",
      busDialog: false,
      driversDialog: false,
      loadingDrivers: false,
      valid: true,
      id: null,
      selectedBus: null,
      license: '',
      licenseRules: [
        v => !!v || this.$t('systemSetup.buses.licenseRequired'),
        v => (v && v.length <= 15) || this.$t('systemSetup.buses.licenseMaxLength'),
      ],
      capacity: '',
      capacityRules: [
        v => /^[0-9]+$/.test(v) || this.$t('systemSetup.buses.capacityInvalid'),
      ],
    };
  },
  computed: {
    headers() {
      return [
        { text: "ID", value: "id", align: "start", filterable: false },
        { text: this.$t('systemSetup.buses.headers.license'), value: "license" },
        { text: this.$t('systemSetup.buses.headers.capacity'), value: "capacity" },
        { text: this.$t('systemSetup.buses.headers.driver'), value: "driver" },
        { text: this.$t('systemSetup.buses.headers.createdAt'), value: "created_at" },
        { text: this.$t('common.actions'), value: "actions", sortable: false },
      ];
    },
  },
  mounted() {
    this.loadBuses();
  },
  methods: {
    loadBuses() {
      this.isLoading = true;
      this.buses = [];
      axios
        .get(`/buses/all`)
        .then((response) => {
          this.buses = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: this.$t('common.error'),
            text: this.$t('systemSetup.buses.notifyLoadError'),
            type: 'error'
          });
          console.log(error);
          auth.checkError(error.response.data.message, this.$router, this.$swal);
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    validate () {
      return this.$refs.form.validate()
    },
    createBus() {
      if(this.validate())
      {
        this.isLoading = true;
        this.busDialog = false;
        axios
          .post(`/buses/create-edit`, {
            bus: {
              id: this.id,
              license: this.license,
              capacity: this.capacity,
            },
          })
          .then((response) => {
            this.loadBuses();
            this.$notify({
              title: this.$t('common.success'),
              text: this.id? this.$t('systemSetup.buses.notifyUpdated') : this.$t('systemSetup.buses.notifyCreated'),
              type: 'success'
            });
            this.$swal(this.$t('common.success'), this.id? this.$t('systemSetup.buses.swalUpdatedText') : this.$t('systemSetup.buses.swalCreatedText'), "success");
          })
          .catch((error) => {
            this.$notify({
              title: this.$t('common.error'),
              text: this.$t('systemSetup.buses.notifyCreateError'),
              type: 'error'
            });
            console.log(error);
            this.$swal(this.$t('common.error'), error.response.data.message, "error");
          })
          .then(() => {
            this.isLoading = false;
          });
      }
    },
    showBusDialog() {
      this.license = '';
      this.capacity = '';
      this.id = null;
      this.busDialog = true;
    },
    editBus(bus) {
      this.id = bus.id;
      this.license = bus.license;
      this.capacity = bus.capacity;
      this.busDialog = true;
    },
    deleteBus(bus, index) {
      this.$swal
        .fire({
          title: this.$t('systemSetup.buses.deleteConfirmTitle'),
          text: this.$t('systemSetup.buses.deleteConfirmText', { license: bus.license }),
          icon: "error",
          showCancelButton: true,
          confirmButtonText: this.$t('systemSetup.buses.confirmDeleteButton'),
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.deleteBusServer(bus.id, index);
          }
        });
    },
    deleteBusServer(bus_id, index) {
      axios
        .delete(`/buses/${bus_id}`)
        .then((response) => {
          this.buses.splice(index, 1);
          this.$notify({
            title: this.$t('common.success'),
            text: this.$t('systemSetup.buses.notifyDeleted'),
            type: "success",
          });
        })
        .catch((error) => {
          this.$notify({
            title: this.$t('common.error'),
            text: this.$t('systemSetup.buses.notifyDeleteError'),
            type: 'error'
          });
          this.$swal(this.$t('common.error'), error.response.data.message, "error");
        })
        .then(() => {
          //this.isDeleting = false;
        });
    },
    getDriverAssignmentColor(driver) {
      if (driver) return "success";
      else return "error";
    },
    getDriver(driver) {
      if (driver) return driver.name;
      else return this.$t('systemSetup.buses.noDriver');
    },
    assignDriver(item) {
      this.selectedBus = item;
      this.driversDialog = true;
      this.loadAvailableDrivers()
    },
    loadAvailableDrivers() {
      this.loadingDrivers = true;
      this.availableDrivers = [];
      axios
        .get('/buses/available-drivers')
        .then((response) => {
          this.availableDrivers = response.data;
        })
        .catch((error) => {
          this.$notify({
            title: this.$t('common.error'),
            text: this.$t('systemSetup.buses.notifyDriversLoadError'),
            type: 'error'
          });
          console.log(error);
          this.$swal(this.$t('common.error'), error.response.data.message, "error");
        })
        .then(() => {
          this.loadingDrivers = false;
        });
    },
    assignDriverToBus(driver) {
      this.loadingDrivers = true;
      axios
        .post(`/buses/assign-driver`, {
          bus_id: this.selectedBus.id,
          driver_id: driver.id,
        })
        .then((response) => {
          this.loadBuses();
          this.$notify({
            title: this.$t('common.success'),
            text: this.$t('systemSetup.buses.notifyDriverAssigned'),
            type: 'success'
          });
          this.$swal(this.$t('common.success'), this.$t('systemSetup.buses.swalDriverAssignedText'), "success");
        })
        .catch((error) => {
          this.$notify({
            title: this.$t('common.error'),
            text: this.$t('systemSetup.buses.notifyDriverAssignError'),
            type: 'error'
          });
          console.log(error);
          this.$swal(this.$t('common.error'), error.response.data.message, "error");
        })
        .then(() => {
          this.loadingDrivers = false;
          this.closeDriverDialog();
        });
    },
    unAssignDriver(item)
    {
      this.$swal
        .fire({
          title: this.$t('systemSetup.buses.unassignConfirmTitle'),
          text: this.$t('systemSetup.buses.unassignConfirmText', { driverName: item.driver.name, license: item.license }),
          icon: "error",
          showCancelButton: true,
          confirmButtonText: this.$t('systemSetup.buses.confirmDeleteButton'),
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.unAssignDriverFromBus(item);
          }
        });
    },
    unAssignDriverFromBus(item) {
      this.isLoading = true;
      axios
        .post(`/buses/unassign-driver`, {
          bus_id: item.id,
        })
        .then((response) => {
          this.loadBuses();
          this.$notify({
            title: this.$t('common.success'),
            text: this.$t('systemSetup.buses.notifyDriverUnassigned'),
            type: 'success'
          });
          this.$swal(this.$t('common.success'), this.$t('systemSetup.buses.swalDriverUnassignedText'), "success");
        })
        .catch((error) => {
          this.$notify({
            title: this.$t('common.error'),
            text: this.$t('systemSetup.buses.notifyDriverUnassignError'),
            type: 'error'
          });
          console.log(error);
          this.$swal(this.$t('common.error'), error.response.data.message, "error");
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    closeDriverDialog() {
      this.driversDialog = false;
      this.loadingDrivers = false;
      this.availableDrivers = [];
    },
  },
};
</script>
